import React, { useEffect, useState } from "react";
import { EuiButtonIcon, EuiFieldText, EuiFormRow } from "@elastic/eui";
import { useApi } from "src/api";
import { AnketaResponse } from "src/api/types";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { useAnketaAuth } from "../context";
import { AnketaResourcesEnum } from "../..";
import { useAnketa } from "src/questionnaires/components/context_provider";
import { OpenQuestion } from "anketa-core/open_question";
import {
  generateJWTToken,
  generateSimpleToken,
} from "../../questionnaires/catalogs/opsanio/token";
import { defaultJwtExpiresIn } from "src/questionnaires/catalogs/opsanio/token/ui";

interface AnketaApiTokenProps {
  questionnaire?: string;
  catalog?: string;
}

export const AnketaApiToken = ({
  questionnaire,
  catalog,
}: AnketaApiTokenProps): JSX.Element => {
  const { Get, Post } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const { keycloak } = useAnketaAuth();
  const ctx = useAnketa();

  const [apiToken, setApiToken] = useState<string>("");

  const isGroupView = questionnaire !== undefined && catalog !== undefined;
  const basePath = isGroupView
    ? `v2/${AnketaResourcesEnum.questionnaires}/${catalog}/${questionnaire}`
    : "v2/auth";

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast({
        title: translate("anketa.helper.copy.success.title", {
          defaultMessage: "Copied to clipboard",
        }),
        color: "success",
        text: translate("anketa.helper.copy.success.text", {
          defaultMessage:
            "The text has been successfully copied to your clipboard.",
        }),
      });
    } catch (err) {
      console.error("Failed to copy text: ", err);
      addToast({
        title: translate("anketa.helper.copy.error.title", {
          defaultMessage: "Copy failed",
        }),
        color: "danger",
        text: translate("anketa.helper.copy.error.text", {
          defaultMessage: "Failed to copy text to clipboard. Please try again.",
        }),
      });
    }
  };

  const updateToken = () => {
    Post<AnketaResponse<any>>({
      path: basePath + "/generate-token",
      responseType: "json",
      token: keycloak.token,
    })
      .then((response) => {
        setApiToken((response.data as any).token);
        const tokenToUpdate = ctx.catalog.children.get("most_token");
        if (tokenToUpdate) {
          try {
            if (typeof crypto !== "undefined" && crypto.subtle) {
              generateJWTToken(
                questionnaire,
                catalog,
                defaultJwtExpiresIn,
                keycloak.token
              )
                .then((token) => {
                  (tokenToUpdate as OpenQuestion).setFactValue(token);
                })
                .catch((error) => {
                  generateSimpleToken(
                    questionnaire,
                    catalog,
                    defaultJwtExpiresIn,
                    "opsanio",
                    keycloak.token
                  ).then((token) => {
                    (tokenToUpdate as OpenQuestion).setFactValue(token);
                  });
                });
            } else {
              generateSimpleToken(
                questionnaire,
                catalog,
                defaultJwtExpiresIn,
                "opsanio",
                keycloak.token
              ).then((token) => {
                (tokenToUpdate as OpenQuestion).setFactValue(token);
              });
            }
          } catch (error) {
            (tokenToUpdate as OpenQuestion).setFactValue(null);
          }
        }
        addToast({
          title: translate("anketa.token.regenerate.modal.success.title", {
            defaultMessage: "Token Regenerated Successfully",
          }),
          color: "success",
          text: translate(
            isGroupView
              ? "anketa.token.regenerate.group.modal.success.text"
              : "anketa.token.regenerate.modal.success.text",
            {
              defaultMessage:
                "Your API token has been successfully updated. The new token is now active.",
            }
          ),
        });
      })
      .catch((err) => {
        console.error("Debug [Error] Failed to regenerate token: ", err);
        addToast({
          title: translate("anketa.token.regenerate.modal.error.title", {
            defaultMessage: "Token Regeneration Failed",
          }),
          color: "danger",
          text: translate("anketa.token.regenerate.modal.error.text", {
            defaultMessage:
              "We couldn't generate a new token. Please try again or contact support if the problem persists.",
          }),
        });
      });
  };

  useEffect(() => {
    Get<any>({
      path: basePath + "/token",
      responseType: "json",
      token: keycloak.token,
    })
      .then((response) => {
        setApiToken(response.data.token);
      })
      .catch((err) => {
        console.error("Debug [Error] Failed to regenerate token: ", err);
      });
  }, []);

  return (
    <>
      {catalog && questionnaire && (
        <EuiFormRow label={"S3 Access Key"}>
          <EuiFieldText
            prepend={
              <EuiButtonIcon
                iconType="copy"
                aria-label={translate("anketa.auth.accessKey.copy.aria", {
                  defaultMessage: "Copy access key",
                })}
                onClick={() => {
                  copyToClipboard(`${catalog}@${questionnaire}.anketa`);
                }}
              />
            }
            readOnly
            value={`${catalog}@${questionnaire}.anketa`}
          />
        </EuiFormRow>
      )}
      <EuiFormRow label={translate("anketa.most.auth.token")}>
        <EuiFieldText
          prepend={
            <EuiButtonIcon
              iconType="copy"
              aria-label={translate("anketa.auth.token.copy.aria", {
                defaultMessage: "Copy token",
              })}
              onClick={() => {
                copyToClipboard(apiToken);
              }}
            />
          }
          append={
            <EuiButtonIcon
              iconType="refresh"
              aria-label={translate("anketa.auth.token.refresh.aria", {
                defaultMessage: "Regenerate token",
              })}
              onClick={() => {
                updateToken();
              }}
            />
          }
          readOnly
          value={apiToken}
        />
      </EuiFormRow>
    </>
  );
};
